import React from 'react';

interface PrimaryButtonProps {
  onClick: () => void;
  text: string;
  variant?: 'gold' | 'dark' | 'white-outline' | 'white' | 'transparent' | 'blue';
  disabled?: boolean;
  size?: 'md' | 'sm';
  fullWidth?: boolean;
  style?: React.CSSProperties;
}

const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  onClick,
  text,
  variant = 'gold',
  disabled = false,
  size = 'sm',
  fullWidth = true,
  style = {},
}) => {
  const getStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      width: fullWidth ? '100%' : 'auto',
      padding: size === 'sm' ? '10px 14px' : '16px 24px',
      borderRadius: size === 'sm' ? '12px' : 'var(--kol-radius-lg, 18px)',
      fontWeight: 500,
      fontSize: size === 'sm' ? '14px' : 'var(--kol-button-font-size, 16px)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.25s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: size === 'sm' ? 6 : 8,
      opacity: disabled ? 0.6 : 1,
    };

    switch (variant) {
      case 'gold':
        return {
          ...baseStyles,
          background: '#E9C546',
          border: 'none',
          borderRadius: 9999,
          color: '#132A49',
          fontWeight: 700,
        };
      case 'dark':
        return {
          ...baseStyles,
          background: '#132A49',
          border: 'none',
          color: '#FFFFFF',
          boxShadow: '0 4px 12px rgba(10, 22, 40, 0.2)',
        };
      case 'white':
        return {
          ...baseStyles,
          background: '#FFFFFF',
          border: '1.5px solid #132A49',
          color: '#132A49',
          boxShadow: '0 2px 8px rgba(10, 22, 40, 0.08)',
        };
      case 'white-outline':
        return {
          ...baseStyles,
          background: 'transparent',
          border: '1.5px solid rgba(255, 255, 255, 0.6)',
          color: '#FFFFFF',
        };
      case 'transparent':
        return {
          ...baseStyles,
          background: 'transparent',
          border: 'none',
          color: '#FFFFFF',
        };
      case 'blue':
        return {
          ...baseStyles,
          background: '#3B7BF4',
          border: 'none',
          color: '#FFFFFF',
          boxShadow: '0 4px 12px rgba(59, 123, 244, 0.3)',
        };
      default:
        return baseStyles;
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`primary-btn variant-${variant}`}
      style={{ ...getStyles(), ...style }}
    >
      {text}
    </button>
  );
};

export default PrimaryButton;
